import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listRecordings, deleteRecording, renameRecording, formatBytes, formatDuration, suggestedFilename } from '../lib/recordingsDb';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Share2, Trash2, Film, Play, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function MyRecordings() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [urls, setUrls] = useState({}); // id -> objectURL
  const [playing, setPlaying] = useState(null);

  const load = async () => {
    const list = await listRecordings();
    setItems(list);
    const map = {};
    for (const r of list) {
      if (r.blob) map[r.id] = URL.createObjectURL(r.blob);
    }
    setUrls(prev => { Object.values(prev).forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); return map; });
  };
  useEffect(() => { load(); return () => { Object.values(urls).forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); }; /* eslint-disable-next-line */ }, []);

  const download = (r) => {
    const a = document.createElement('a');
    a.href = urls[r.id]; a.download = suggestedFilename(r);
    document.body.appendChild(a); a.click(); a.remove();
  };
  const share = async (r) => {
    try {
      const file = new File([r.blob], suggestedFilename(r), { type: r.mime_type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: r.title, text: 'My Jixxeo AI recording' });
      } else {
        download(r);
      }
    } catch {}
  };
  const del = async (id) => {
    if (!window.confirm('Delete this recording?')) return;
    await deleteRecording(id); toast.success('Deleted'); load();
  };
  const rename = async (r) => {
    const t = window.prompt('Rename recording', r.title);
    if (!t) return;
    await renameRecording(r.id, t); toast.success('Renamed'); load();
  };

  if (!user) return (
    <div className="max-w-md mx-auto text-center py-16">
      <Film className="w-12 h-12 mx-auto text-slate-500 mb-3" />
      <div className="font-display text-xl font-bold">Sign in to see your recordings</div>
      <Link to="/login"><Button className="mt-4 gradient-brand text-white">Sign in</Button></Link>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2"><Film className="w-6 h-6 text-violet-400" /> My Recordings</h1>
        <p className="text-sm text-slate-400">Your Jixxeo AI recordings — saved locally on this device</p>
      </div>
      {items.length === 0 && (
        <Card className="glass p-10 text-center">
          <Film className="w-12 h-12 mx-auto text-slate-500 mb-3" />
          <div className="font-display text-lg font-bold">No recordings yet</div>
          <div className="text-sm text-slate-400 mt-1">Tap the floating <span className="text-red-400 font-semibold">Record</span> button to capture a Jixxeo AI session</div>
        </Card>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map(r => {
          const isAudio = r.kind === 'audio' || (r.mime_type || '').startsWith('audio/');
          return (
          <Card key={r.id} data-testid={`rec-card-${r.id}`} className="glass overflow-hidden">
            <div className="aspect-video bg-black relative">
              {urls[r.id] && !isAudio && (
                playing === r.id
                  ? <video src={urls[r.id]} controls autoPlay className="w-full h-full object-contain" />
                  : (
                    <video
                      src={urls[r.id]}
                      preload="metadata"
                      className="w-full h-full object-cover"
                      onClick={() => setPlaying(r.id)}
                    />
                  )
              )}
              {isAudio && urls[r.id] && (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-violet-900/50 to-slate-900">
                  <div className="w-14 h-14 rounded-full gradient-brand flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zm-7 9a1 1 0 1 0-2 0 9 9 0 0 0 8 8.94V22a1 1 0 1 0 2 0v-2.06A9 9 0 0 0 21 11a1 1 0 1 0-2 0 7 7 0 0 1-14 0z"/></svg>
                  </div>
                  <audio src={urls[r.id]} controls className="w-[90%]" />
                </div>
              )}
              {!isAudio && playing !== r.id && (
                <button data-testid={`rec-play-${r.id}`} onClick={() => setPlaying(r.id)} className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/20 transition-colors">
                  <Play className="w-10 h-10 text-white" />
                </button>
              )}
              <div className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-red-600/90 text-white px-2 py-0.5 rounded font-bold">Jixxeo AI</div>
              <div className="absolute bottom-2 right-2 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded font-mono">{formatDuration(r.duration_sec)}</div>
            </div>
            <div className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div data-testid={`rec-title-${r.id}`} className="font-semibold flex-1 truncate">{r.title}</div>
                <button data-testid={`rec-rename-${r.id}`} onClick={() => rename(r)} className="text-slate-400 hover:text-white p-1"><Pencil className="w-3.5 h-3.5" /></button>
              </div>
              <div className="text-xs text-slate-400 flex gap-3">
                <span>{new Date(r.created_at).toLocaleDateString()}</span>
                <span>{formatBytes(r.size)}</span>
                <span className="uppercase">{isAudio ? (r.mime_type.includes('mp4') ? 'M4A' : 'WEBM') : (r.mime_type.includes('mp4') ? 'MP4' : 'WEBM')}</span>
              </div>
              <div className="flex gap-2 pt-1">
                <Button data-testid={`rec-download-${r.id}`} size="sm" onClick={() => download(r)} className="gradient-brand text-white flex-1"><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
                <Button data-testid={`rec-share-${r.id}`} size="sm" variant="outline" onClick={() => share(r)}><Share2 className="w-3.5 h-3.5" /></Button>
                <Button data-testid={`rec-delete-${r.id}`} size="sm" variant="outline" onClick={() => del(r.id)} className="text-red-400 border-red-500/30 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </Card>
        );})}
      </div>
    </div>
  );
}
